import { AxiosResponse } from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import qs from 'query-string';
import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import { usePrefetchQueryManualKey } from 'hooks/usePrefetchQuery';
import { KTreeNode } from 'types/api/Tenant/AROKMS/KTreeDataTypes';
import {
  ChartDisplay,
  ChartViewPanelOptions,
  ResponseChartViewDisplay,
} from 'types/api/Tenant/AROChart/ChartViewTypes';
import { createTreeData, sortTreeData } from 'utils/Object';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { ENV_CONFIG } from 'config/env';

export const CHART_VIEW_DISPLAY_QUERY_KEY = {
  CHART_VIEW_DISPLAY: 'chart-view-display',
  CHART_VIEW_ITEM_DATA: 'chart-view-item-data',
  CHART_VIEW_PANLE_OPTIONS: 'chart-view-panel-options',
};

export function useGetChartViewLayout(payload: { chartViewId?: string }) {
  return useQuery<AxiosResponse<ResponseChartViewDisplay>, AxiosDefaultErrorEntity>(
    [CHART_VIEW_DISPLAY_QUERY_KEY.CHART_VIEW_DISPLAY, payload],
    () => axios.get(`/api/v1/tenant/chart-view/display/${payload.chartViewId}`).then((res) => res),
    {
      enabled: !!payload.chartViewId,
    }
  );
}

export function useGetChartItemData(payload: {
  chartViewItemId?: string;
  memberIds: string[];
  cubeAttributeId: string;
  accumulationType: string;
  filters: AutoCompleteItem[];
  insightViewAttributeIds?: string[];
  myViewId?: string;
}) {
  return useQuery<AxiosResponse<ChartDisplay>, AxiosDefaultErrorEntity>(
    [CHART_VIEW_DISPLAY_QUERY_KEY.CHART_VIEW_ITEM_DATA, payload],
    () =>
      axios
        .post(`/api/v1/tenant/chart-view/data/${payload.chartViewItemId}`, {
          members: payload.memberIds,
          cubeAttributeId: payload.cubeAttributeId,
          accumulationType: payload.accumulationType,
          insightAttributeIds: payload.insightViewAttributeIds,
          filters: payload.filters ?? [],
          myViewId: payload.myViewId,
        })
        .then((res) => res),
    {
      enabled:
        !!payload.chartViewItemId &&
        !!payload.memberIds &&
        payload.memberIds.length > 0 &&
        !!payload.cubeAttributeId &&
        (!payload.insightViewAttributeIds || payload.insightViewAttributeIds.length > 0),
      cacheTime: ENV_CONFIG.endpoint.MAXIMUM_CACHE_TIME,
      staleTime: ENV_CONFIG.endpoint.MAXIMUM_CACHE_TIME,
    }
  );
}

export function useGetChartViewPanelOptions(chartViewId?: string, myViewId?: string, memberId?: string) {
  return useQuery<AxiosResponse<ChartViewPanelOptions>, AxiosDefaultErrorEntity>(
    [CHART_VIEW_DISPLAY_QUERY_KEY.CHART_VIEW_PANLE_OPTIONS, chartViewId, memberId],
    () =>
      axios
        .get(
          `/api/v1/tenant/chart-view/panel-options/${chartViewId}?${qs.stringify({
            myViewId,
            memberId,
          })}`
        )
        .then((res) => {
          const treeData = createTreeData(null, res.data.memberOptions);
          return {
            ...res,
            data: {
              ...res.data,
              memberOptions: sortTreeData(treeData),
            },
          };
        }),
    {
      enabled: !!chartViewId,
    }
  );
}

// ── Prefetch hooks for dashboard navigation ──────────────────────────
export function usePrefetchChartViewLayout() {
  return usePrefetchQueryManualKey(
    (chartViewId?: string) =>
      axios.get(`/api/v1/tenant/chart-view/display/${chartViewId}`).then((res) => res),
    { staleTime: 100000 }
  );
}

export function usePrefetchChartViewPanelOptions() {
  return usePrefetchQueryManualKey(
    (chartViewId?: string) =>
      axios
        .get(`/api/v1/tenant/chart-view/panel-options/${chartViewId}`)
        .then((res) => {
          const treeData = createTreeData(null, res.data.memberOptions);
          return {
            ...res,
            data: {
              ...res.data,
              memberOptions: sortTreeData(treeData),
            },
          };
        }),
    { staleTime: 100000 }
  );
}

/**
 * Chained prefetch: fetches chart layout + panel options, then uses the
 * default member/measure/accumulation to prefetch every chart-item's data.
 */
export function usePrefetchChartFullData() {
  const queryClient = useQueryClient();

  return (chartViewId: string, myViewId?: string) => {
    const layoutPromise = queryClient.fetchQuery(
      [CHART_VIEW_DISPLAY_QUERY_KEY.CHART_VIEW_DISPLAY, { chartViewId }],
      () => axios.get(`/api/v1/tenant/chart-view/display/${chartViewId}`).then((res) => res),
      { staleTime: 100000 }
    );

    const panelPromise = queryClient.fetchQuery(
      [CHART_VIEW_DISPLAY_QUERY_KEY.CHART_VIEW_PANLE_OPTIONS, chartViewId, undefined],
      () =>
        axios
          .get(
            `/api/v1/tenant/chart-view/panel-options/${chartViewId}?${qs.stringify({
              myViewId,
            })}`
          )
          .then((res) => {
            const treeData = createTreeData(null, res.data.memberOptions);
            return {
              ...res,
              data: { ...res.data, memberOptions: sortTreeData(treeData) },
            };
          }),
      { staleTime: 100000 }
    );

    Promise.all([layoutPromise, panelPromise]).then(([layoutRes, panelRes]) => {
      const charts: AutoCompleteItem[] = layoutRes.data.charts ?? [];
      const panel = panelRes.data;

      // Derive default member IDs (second-highest level, matching page logic)
      const flatMembers: KTreeNode[] = panel.memberOptions ?? [];
      const levels = flatMembers.map((m: KTreeNode) => m.level);
      const uniqueLevels = Array.from(new Set(levels)).sort((a, b) => b - a);
      const targetLevel = uniqueLevels.length > 1 ? uniqueLevels[1] : uniqueLevels[0];
      const defaultMembers = flatMembers.filter((m: KTreeNode) => m.level === targetLevel);
      const memberIds = defaultMembers.map((m: KTreeNode) => m.value.toString());

      // Derive default measure & accumulation
      const measure = panel.selectedMeasurement ?? panel.measureOptions?.[0];
      const accumulation = panel.accumulationOptions?.[0];
      const cubeAttributeId = measure?.value?.toString() ?? '';
      const accumulationType = accumulation?.value?.toString() ?? '';

      // Derive default insight attributes
      const insightViewAttributeIds = panel.insightAttributeOptions
        ?.filter((item: { type?: string }) => item.type === accumulationType)
        ?.slice(0, 6)
        ?.map((item: AutoCompleteItem) => item.value.toString());

      if (!memberIds.length || !cubeAttributeId) return;

      // Prefetch each chart item's data
      charts.forEach((chart) => {
        const payload = {
          chartViewItemId: chart.value.toString(),
          memberIds,
          cubeAttributeId,
          accumulationType,
          insightViewAttributeIds,
          filters: [] as AutoCompleteItem[],
          myViewId,
        };
        queryClient.prefetchQuery(
          [CHART_VIEW_DISPLAY_QUERY_KEY.CHART_VIEW_ITEM_DATA, payload],
          () =>
            axios
              .post(`/api/v1/tenant/chart-view/data/${payload.chartViewItemId}`, {
                members: payload.memberIds,
                cubeAttributeId: payload.cubeAttributeId,
                accumulationType: payload.accumulationType,
                insightAttributeIds: payload.insightViewAttributeIds,
                filters: [],
                myViewId,
              })
              .then((res) => res),
          {
            staleTime: ENV_CONFIG.endpoint.MAXIMUM_CACHE_TIME,
          }
        );
      });
    });
  };
}
